import moment from "moment";
import {
    GroupByOption,
    nextBucketStart,
    type DateGroupByGrain,
    type GroupByKind,
    type GroupByOptionValue,
    type NumericRangeGroupByOption,
    type NumericRangeMode,
    type SignalsRequest,
    type SignalsTemporalLabels,
} from "components/Signals/SignalsModels";
import {
    allOutlineSegmentOptions,
    type IOutlineFilter,
} from "components/Conversations/Views/Drawer/components/DeepFilter/OutlineFilterStore";
import type { IRBCFilter } from "components/Conversations/Views/Drawer/components/DeepFilter/RBCFilterStore";
import type {
    ApplicationFilters,
    DataLabel,
    PrimaryDriver,
    PrimaryTopic,
    SimpleTheme,
    SimpleTopicCluster,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import type OrgOptionsStore from "stores/OrgOptionsStore";
import { CallDirection } from "stores/ApplicationFilters/Filters/CallDirection";
import { EvaluationType } from "stores/ApplicationFilters/Filters/EvaluationType";
import { HipaaCompliance } from "stores/ApplicationFilters/Filters/HipaaCompliance";
import { Language } from "stores/ApplicationFilters/Filters/Language";
import { MediaType } from "stores/ApplicationFilters/Filters/MediaType";
import {
    type SafetyEvent,
    SafetyEventAck,
    SafetyEventId,
    SafetyEventType,
    SafetyEventTypeOperator,
} from "stores/ApplicationFilters/Filters/SafetyEvent";
import { Sentiment } from "stores/ApplicationFilters/Filters/Sentiment";
import { PatientJourneyStage } from "stores/ApplicationFilters/Filters/PatientJourneyStage";
import type {
    DrilldownSelection,
    DrilldownSource,
} from "./DrilldownTypes";

type ActiveGrouping = {
    groupByOption: GroupByOptionValue;
    groupByIds: string[];
    groupByLlmClassifierId?: string;
    groupByExtendedMetadataKey?: string;
    groupByQuestionId?: string;
    groupByLicensedModuleId?: string;
};

type GroupingSelection = {
    label?: string;
    id?: string;
};

type GroupingContext = {
    source: DrilldownSource;
    selection: DrilldownSelection;
    applicationFilterValuesStore?: ApplicationFilterValuesStore;
    optionsStore?: OrgOptionsStore;
};

const toOptionalString = (value?: string | number | null) => {
    if (value === undefined || value === null) return undefined;
    const normalized = `${value}`.trim();
    return normalized.length ? normalized : undefined;
};

const isSwappedGroupingAndX = (source: DrilldownSource) => {
    const isTemporal = source.config?.renderOptions?.isTemporal ?? false;
    if (isTemporal) return false; // temporal charts never have swapped grouping and x set to true
    return (
        source.parsedVizOptions?.swapGroupingAndX ?? true // if swap grouping and x doesn't exist on viz options, we assume it to be true (catches old model results charts grouped by agent)
    );
};

/**
 * What kind of grouping the chart carries, if any.
 *
 * Separate from `getActiveGrouping`, which answers a narrower question. Turning a selected group
 * into an application filter is categorical-only; knowing which axis the grouping sits on follows
 * from the chart being grouped at all, so it has to see date and range groupings too.
 */
const getActiveGroupingKind = (
    source: DrilldownSource,
): GroupByKind | undefined => {
    const state = source.state as SignalsRequest | null | undefined;
    return state?.groupBy?.kind;
};

const getActiveGrouping = (
    source: DrilldownSource,
): ActiveGrouping | undefined => {
    const state = source.state as SignalsRequest | null | undefined;
    const grouping = state?.groupBy;

    // This path turns the selected group back into an application filter, which only a categorical
    // grouping has: a value id becomes a filter on that value. A date bucket narrows the request's
    // date range instead, which DrilldownStore does alongside its temporal handling because that is
    // where the chart's date bounds and reference-date conversion live.
    if (grouping?.kind !== "categorical" || grouping.valueIds.length === 0) {
        return undefined;
    }

    return {
        groupByOption: grouping.option,
        groupByIds: grouping.valueIds,
        groupByLlmClassifierId: grouping.llmClassifierId,
        groupByExtendedMetadataKey: grouping.extendedMetadataKey,
        groupByQuestionId: grouping.questionId,
        groupByLicensedModuleId: grouping.licensedModuleId,
    };
};

const TEMPORAL_LABEL_BY_GRAIN: Record<
    Exclude<DateGroupByGrain, "auto">,
    SignalsTemporalLabels
> = {
    day: "Daily",
    week: "Weekly",
    month: "Monthly",
    quarter: "Quarterly",
};

/** How wide one date bucket is, per grain. */
const DATE_BUCKET_UNIT_BY_GRAIN: Record<
    SignalsTemporalLabels,
    moment.unitOfTime.DurationConstructor
> = {
    Daily: "day",
    Weekly: "week",
    Monthly: "month",
    Quarterly: "quarter",
};

/**
 * The span a date bucket covers, from the ISO date it starts on, narrowed to the chart's own date
 * range.
 *
 * The narrowing is what makes a drilldown count match the bar it came from. A chart's first and last
 * buckets are usually partial. A weekly bucket starting on the 26th, on a chart that begins on the
 * 1st, holds one day of data rather than seven, so a drilldown covering the whole bucket would
 * report six days the chart never counted.
 *
 * A bucket that does not overlap the range at all keeps its own span. That happens when the chart
 * filters on something other than the interaction date, since buckets are always cut on the
 * interaction date. The two are then measured on different columns, so narrowing would produce a
 * span starting after it ends and match nothing.
 *
 * Returns undefined when the start is not a date, so callers leave the drilldown unnarrowed.
 */
export const getDateBucketSpan = (
    bucketStart: string,
    grain: SignalsTemporalLabels,
    chartRange?: { start?: moment.Moment; end?: moment.Moment },
): { start: moment.Moment; end: moment.Moment } | undefined => {
    const start = moment(bucketStart);
    if (!start.isValid()) return undefined;

    // One grain forward then a day back is the last day the bucket covers, for every grain.
    const end = start
        .clone()
        .add(1, DATE_BUCKET_UNIT_BY_GRAIN[grain])
        .subtract(1, "day")
        .endOf("day");

    const rangeStart = chartRange?.start;
    const rangeEnd = chartRange?.end;
    const narrowedStart =
        rangeStart?.isValid() && rangeStart.isAfter(start)
            ? rangeStart.clone()
            : start;
    const narrowedEnd =
        rangeEnd?.isValid() && rangeEnd.isBefore(end) ? rangeEnd.clone() : end;

    return narrowedStart.isAfter(narrowedEnd)
        ? { start, end }
        : { start: narrowedStart, end: narrowedEnd };
};

/**
 * The configured grain as the temporal vocabulary the drilldown date helpers speak.
 *
 * `auto` is the one grain this cannot answer alone, because it is defined by the request's date
 * range. The caller supplies it, being the one that holds the chart's bounds.
 */
export const resolveDateGroupingTemporalGrain = (
    grain: DateGroupByGrain,
    resolveAuto: () => SignalsTemporalLabels,
): SignalsTemporalLabels =>
    grain === "auto" ? resolveAuto() : TEMPORAL_LABEL_BY_GRAIN[grain];

/** The identifier shape a date bucket travels as: the ISO date the bucket starts on. */
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;

/**
 * The date bucket a selection landed on, for a chart grouped by date.
 *
 * Only the bucket's identifier is read, never the label beside it. The identifier is the ISO date
 * the bucket starts on; the label is the span the bucket covers ("Jan 4 - Jan 10"), which no date
 * parser recovers a date from. A selection carrying no identifier returns undefined so the caller
 * leaves the drilldown on the chart's own range, rather than narrowing it to a date read out of a
 * span.
 *
 * Which half of the selection holds the bucket follows the same orientation rule as categorical
 * grouping: x when swapped, series otherwise. On the x side the identifier arrives in `xLabel`,
 * which despite its name is the clicked point's raw value rather than its text: `selectDataPoint`
 * fills it from the Google DataTable's `x.v` cell while `x` holds the formatted `x.f` label. A
 * chart click never populates `xIdentifier` — only TreeMap does — so reading it alone leaves every
 * ordinary click on a swapped date-grouped chart unnarrowed. Categorical and numeric range grouping
 * read the same pair.
 *
 * The identifier's shape is checked rather than assumed, so a label that reached here is rejected
 * instead of being handed to `moment` as a loose string and narrowing the drilldown to whatever it
 * made of the text.
 */
export const getDateGroupingSelection = ({
    source,
    selection,
}: Pick<GroupingContext, "source" | "selection">):
    | { grain: DateGroupByGrain; bucketStart: string }
    | undefined => {
    const state = source.state as SignalsRequest | null | undefined;
    const grouping = state?.groupBy;

    if (grouping?.kind !== "date") {
        return undefined;
    }

    const bucketStart = isSwappedGroupingAndX(source)
        ? (selection.xIdentifier ?? selection.xLabel)
        : selection.seriesIdentifier;

    if (!bucketStart || !ISO_DATE_PATTERN.test(bucketStart.trim())) {
        return undefined;
    }

    return { grain: grouping.grain, bucketStart: bucketStart.trim() };
};

/**
 * How a range dimension is cut, as the group-by catalog publishes it.
 *
 * Read rather than restated so the frontend and the bucketing SQL cannot disagree about which
 * dimensions are discrete. Falls back to continuous when the catalog has not loaded, matching the
 * backend's own default, which keeps a drilldown at the bucket's shared-boundary reading rather than
 * a whole unit inside it.
 */
const numericRangeModeFor = (
    option: NumericRangeGroupByOption,
    optionsStore: OrgOptionsStore | undefined,
): NumericRangeMode =>
    optionsStore?.signalsGroupByCatalog.find(
        (item) => item.groupByOption === option,
    )?.numericRangeMode ?? "continuous";

/**
 * The range bucket a selection landed on, for a chart grouped by a numeric range.
 *
 * Reads the bucket's identifier and nothing else. A date bucket's label can be parsed back into the
 * date it names; a range bucket's label is the span it covers, "2–5 min", while its identifier is
 * the bucket's ordinal. The two are not interchangeable, and a label that reached here would parse
 * as a real bucket rather than failing, so the identifier's shape is checked rather than assumed.
 *
 * Which half of the selection holds the bucket follows the same orientation rule as categorical
 * grouping: x when swapped, series otherwise. On the x side the ordinal arrives in `xLabel`, which
 * despite its name is the clicked point's raw value rather than its text. A chart click never
 * populates `xIdentifier`, and on a swapped chart the x axis is the grouping, so the raw value is
 * the bucket's ordinal while `x` holds the label. Categorical grouping reads the same pair.
 */
export const getNumericRangeGroupingSelection = ({
    source,
    selection,
    optionsStore,
}: Pick<GroupingContext, "source" | "selection" | "optionsStore">):
    | {
          option: NumericRangeGroupByOption;
          cutPoints: number[];
          bucketIndex: number;
          /** Carried alongside the option so callers read the bucket's edges from one place. */
          mode: NumericRangeMode;
          /** Module Score only: whose score the buckets divide. Undefined for the others. */
          moduleId?: string;
      }
    | undefined => {
    const state = source.state as SignalsRequest | null | undefined;
    const grouping = state?.groupBy;

    if (grouping?.kind !== "numericRange") {
        return undefined;
    }

    const identifier = isSwappedGroupingAndX(source)
        ? (selection.xIdentifier ?? selection.xLabel)
        : selection.seriesIdentifier;
    if (!identifier) return undefined;

    // Digits and nothing else, checked before parsing rather than after. `Number.parseInt` reads a
    // leading number and discards the rest, so a label reaching this by mistake parses as the bucket
    // its first number names: "2-5 min" would drill into bucket 2.
    const raw = identifier.trim();
    if (!/^\d+$/.test(raw)) return undefined;

    const bucketIndex = Number.parseInt(raw, 10);

    return {
        option: grouping.option,
        cutPoints: grouping.cutPoints,
        bucketIndex,
        mode: numericRangeModeFor(grouping.option, optionsStore),
        moduleId: grouping.moduleId,
    };
};

/**
 * The span a range bucket covers, from its ordinal and the chart's cut points.
 *
 * `lowerBound` is always the first value in the bucket. Where that falls is the range's mode: the
 * cut point below it for a continuous range, a whole unit past that for a discrete one. That is the
 * same rule the chart labelled the bar with, so the filter and the bar start together.
 *
 * `upperBound` is the cut point the bucket ends on, and the *caller* decides how to compare against
 * it, because that follows from the same mode: a continuous bucket excludes it ("2-5 min" holds
 * 4:59.999, so call duration sends an exclusive max), and a discrete one includes it ("6-10
 * messages" holds 10, so message count sends its ordinary inclusive max). Both dimensions' filters
 * then select exactly the rows their bar counted.
 *
 * It is absent for the last bucket, which runs from its boundary upward: the "20+ min" bar.
 * Callers must leave their upper filter unset in that case rather than sending a zero, which the
 * platform's duration and message-count filters read as a real bound and would match nothing.
 *
 * Returns undefined for an ordinal the cut points cannot describe, so a stale selection leaves the
 * drilldown unnarrowed rather than narrowing it to a range the chart never drew.
 */
export const getNumericRangeBucketBounds = (
    cutPoints: number[],
    bucketIndex: number,
    mode: NumericRangeMode,
): { lowerBound: number; upperBound?: number } | undefined => {
    if (
        !Number.isInteger(bucketIndex) ||
        bucketIndex < 0 ||
        bucketIndex > cutPoints.length
    ) {
        return undefined;
    }

    return {
        // The first bucket starts at zero rather than at a cut point, matching the label the chart
        // shows: every dimension bucketed this way measures something non-negative.
        lowerBound:
            bucketIndex === 0
                ? 0
                : nextBucketStart(cutPoints[bucketIndex - 1], mode),
        upperBound:
            bucketIndex === cutPoints.length ? undefined : cutPoints[bucketIndex],
    };
};

const getGroupingSelection = ({
    source,
    selection,
}: GroupingContext): GroupingSelection | undefined => {
    if (!getActiveGrouping(source)) {
        return undefined;
    }

    if (isSwappedGroupingAndX(source)) {
        return {
            label: toOptionalString(selection.x),
            id: selection.xIdentifier ?? selection.xLabel,
        };
    }

    return {
        label: toOptionalString(selection.series),
        id: selection.seriesIdentifier,
    };
};

/**
 * Whether something other than the chart's own dimension is occupying one of its axes.
 *
 * A categorical grouping with nothing selected yet is not grouping the chart, so it must not move
 * the strategy's axes. A date or range grouping needs no selection to be active, so having one is
 * enough.
 */
const hasGroupingOnAxis = (source: DrilldownSource): boolean => {
    const kind = getActiveGroupingKind(source);
    return kind === "categorical" ? !!getActiveGrouping(source) : !!kind;
};

const getStrategySelection = (
    source: DrilldownSource,
    selection: DrilldownSelection,
): DrilldownSelection => {
    if (!hasGroupingOnAxis(source) || !isSwappedGroupingAndX(source)) {
        return selection;
    }

    return {
        ...selection,
        x: selection.series ?? selection.x,
        series: null,
        seriesIdentifier: undefined,
        xIdentifier: selection.seriesIdentifier ?? selection.xIdentifier,
    };
};

const resolveJourneyStage = (
    label?: string,
): PatientJourneyStage | undefined => {
    switch (label) {
        case "Before Treatment":
            return PatientJourneyStage.BeforeTreatment;
        case "On Treatment":
            return PatientJourneyStage.OnTreatment;
        case "After Treatment":
            return PatientJourneyStage.AfterTreatment;
        case "Restarting Treatment":
            return PatientJourneyStage.RestartingTreatment;
        case "Treatment Disruption":
            return PatientJourneyStage.TreatmentDisruption;
        case "No Clear Journey Stage":
            return PatientJourneyStage.NoClearJourneyStage;
        default:
            return undefined;
    }
};

const resolveContactType = (
    label: string,
    applicationFilterValuesStore?: ApplicationFilterValuesStore,
) => {
    const mappedContactType = applicationFilterValuesStore?.mappedCustomerTypes.find(
        (item) => item.name === label || item.sourceName === label,
    );

    return mappedContactType?.sourceName ?? label;
};

const resolveHipaaCompliance = (
    label?: string,
): HipaaCompliance | undefined => {
    switch (label) {
        case "Compliant":
            return HipaaCompliance.Compliant;
        case "Not Compliant":
            return HipaaCompliance.NotCompliant;
        case "N/A":
        case "Not Applicable":
            return HipaaCompliance.NotApplicable;
        default:
            return undefined;
    }
};

const resolveSentiment = (label?: string): Sentiment | undefined => {
    switch (label) {
        case "Positive":
            return Sentiment.Positive;
        case "Neutral":
            return Sentiment.Neutral;
        case "Negative":
            return Sentiment.Negative;
        default:
            return undefined;
    }
};

const resolveCallDirection = (
    label?: string,
): CallDirection | undefined => {
    if (!label) return undefined;

    const normalized = label.replace(/\s+/g, "");
    switch (normalized) {
        case "Inbound":
            return CallDirection.Inbound;
        case "Outbound":
            return CallDirection.Outbound;
        case "Transfer":
            return CallDirection.Transfer;
        case "Other":
            return CallDirection.Other;
        default:
            return undefined;
    }
};

const resolveEvaluationType = (
    label?: string,
    id?: string,
): EvaluationType | undefined => {
    const normalized = (id || label || "").replace(/\s+/g, "").toLowerCase();
    switch (normalized) {
        case "middleofthefunnel":
        case "aievaluated":
        case "ai":
            return EvaluationType.AIEvaluated;
        case "bottomofthefunnel":
        case "humanevaluated":
        case "human":
            return EvaluationType.HumanEvaluated;
        case "notevaluated":
        case "noevaluation":
            return EvaluationType.NotEvaluated;
        default:
            return undefined;
    }
};

const resolveMediaType = (label?: string, id?: string): MediaType | undefined => {
    const normalized = (id || label || "").replace(/\s+/g, "").toLowerCase();
    switch (normalized) {
        case "audio":
        case "calls":
            return MediaType.Audio;
        case "processedchat":
        case "chat":
        case "chats":
            return MediaType.ProcessedChat;
        default:
            return undefined;
    }
};

const resolveLanguage = (label?: string, id?: string) => {
    const normalized = (id || label || "").trim().toLowerCase();

    if (normalized === "english") {
        return {
            languageValue: Language.English,
            localeId: 1033,
        };
    }

    if (
        normalized === "non-english" ||
        normalized === "nonenglish" ||
        normalized === "non english"
    ) {
        return {
            languageValue: Language.NonEnglish,
        };
    }

    return undefined;
};

const resolveEddyEffect = (label?: string): boolean | undefined => {
    if (!label) return undefined;

    const normalized = label.trim().toLowerCase();
    if (
        normalized === "identified" ||
        normalized === "eddy identified" ||
        normalized === "present" ||
        normalized === "true"
    ) {
        return true;
    }

    if (
        normalized === "not identified" ||
        normalized === "notidentified" ||
        normalized === "eddy not identified" ||
        normalized === "not_present" ||
        normalized === "not present" ||
        normalized === "false"
    ) {
        return false;
    }

    return undefined;
};

const resolveSafetyEvent = (label?: string) => {
    if (!label) return undefined;

    const baseSafetyEvent: SafetyEvent = {
        identification: null,
        typeOperator: SafetyEventTypeOperator.Or,
        acknowledgments: [],
        types: [],
    };

    switch (label) {
        case "Identified":
            return {
                ...baseSafetyEvent,
                identification: SafetyEventId.Identified,
            };
        case "Not Identified":
            return {
                ...baseSafetyEvent,
                identification: SafetyEventId.NotIdentified,
            };
        case "Acknowledged":
            return {
                ...baseSafetyEvent,
                acknowledgments: [SafetyEventAck.Acknowledged],
            };
        case "Not Acknowledged":
            return {
                ...baseSafetyEvent,
                acknowledgments: [SafetyEventAck.NotAcknowledged],
            };
        case "Adverse Event":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.AdverseEvent],
            };
        case "Product Quality Complaint":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.ProductQualityComplaint],
            };
        case "Special Situation":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.SpecialSituation],
            };
        case "Not Adverse Event":
            return {
                ...baseSafetyEvent,
                types: [SafetyEventType.NotAdverseEvent],
            };
        default:
            return undefined;
    }
};

const buildOutlineFilterItems = (
    segmentId?: string,
    label?: string,
): IOutlineFilter[] | undefined => {
    const normalizedSegmentId =
        segmentId ??
        allOutlineSegmentOptions.find((option) => option.name === label)?.id;

    if (!normalizedSegmentId) {
        return undefined;
    }

    return [
        {
            value: true,
            segments: [normalizedSegmentId],
            filterGroupId: 0,
        },
    ];
};

const buildTopicFilter = (
    id?: string,
    label?: string,
): SimpleTopicCluster[] | undefined => {
    if (!label) return undefined;

    return [
        {
            id: id ?? label,
            topicLabel: label,
        },
    ];
};

const buildThemeFilter = (
    id?: string,
    label?: string,
): SimpleTheme[] | undefined => {
    if (!label) return undefined;

    return [
        {
            id: id ?? label,
            themeLabel: label,
        },
    ];
};

const buildPrimaryTopicFilter = (
    id?: string,
    label?: string,
): PrimaryTopic[] | undefined => {
    const topicNumber = id ? Number.parseInt(id, 10) : Number.NaN;
    if (!label || Number.isNaN(topicNumber)) return undefined;

    return [
        {
            topicNumber,
            topicLabel: label,
        },
    ];
};

const buildPrimaryDriverFilter = (
    id?: string,
    label?: string,
): PrimaryDriver[] | undefined => {
    const driverIndex = id ? Number.parseInt(id, 10) : Number.NaN;
    if (!label || Number.isNaN(driverIndex)) return undefined;

    return [
        {
            driverIndex,
            driverName: label,
        },
    ];
};

const buildClassifierFilter = (id?: string): IRBCFilter[] | undefined => {
    if (!id) return undefined;

    return [
        {
            value: true,
            classifierIds: [id],
            combinator: 0,
            filterGroupId: 0,
        },
    ];
};

const applyGroupingSelectionToFiltersInternal = ({
    source,
    selection,
    filters,
    applicationFilterValuesStore,
    optionsStore,
}: GroupingContext & {
    filters: ApplicationFilters;
}) => {
    const grouping = getActiveGrouping(source);
    const groupingSelection = getGroupingSelection({
        source,
        selection,
        applicationFilterValuesStore,
    });

    if (!grouping || !groupingSelection) {
        return;
    }

    const {
        groupByOption,
        groupByLlmClassifierId,
        groupByExtendedMetadataKey,
        groupByQuestionId,
        groupByLicensedModuleId,
    } = grouping;
    const { label, id } = groupingSelection;

    switch (groupByOption) {
        case GroupByOption.Agent:
            if (id) {
                filters.agentIds = [id];
            }
            break;
        // The grouping identifier is the manager id the filter selects on, so the drilldown
        // reproduces the bar exactly rather than approximating it from the label.
        case GroupByOption.AgentManager:
            if (id) {
                filters.agentManagerIds = [id];
            }
            break;
        case GroupByOption.Hierarchy:
            if (id) {
                filters.hierarchyIds = [id];
            }
            break;
        case GroupByOption.ConversationLabeling:
            if (id && label) {
                const dataLabel: DataLabel = { id, name: label };
                filters.dataLabels = [dataLabel];
                filters.containsDataLabels = true;
            }
            break;
        case GroupByOption.EvaluationType: {
            const evaluationType = resolveEvaluationType(label, id);
            if (evaluationType !== undefined) {
                filters.evaluationTypes = [evaluationType];
            }
            break;
        }
        case GroupByOption.Language: {
            const language = resolveLanguage(label, id);
            if (language) {
                filters.languages = [language];
            }
            break;
        }
        case GroupByOption.MediaType: {
            const mediaType = resolveMediaType(label, id);
            if (mediaType !== undefined) {
                filters.mediaTypes = [mediaType];
            }
            break;
        }
        // Each Theme grouping drills into its own filter, so the drilldown stays on the same Theme
        // contract the grouping used.
        case GroupByOption.PrimaryThemes: {
            const topics = buildTopicFilter(id, label);
            if (topics?.length) {
                filters.topics = topics;
                filters.containsTopics = true;
            }
            break;
        }
        case GroupByOption.Themes: {
            const themes = buildThemeFilter(id, label);
            if (themes?.length) {
                filters.themes = themes;
                filters.containsThemes = true;
            }
            break;
        }
        case GroupByOption.PrimaryTopic: {
            const primaryTopics = buildPrimaryTopicFilter(id, label);
            if (primaryTopics?.length) {
                filters.primaryTopics = primaryTopics;
                filters.containsPrimaryTopics = true;
            }
            break;
        }
        case GroupByOption.PrimaryDriver: {
            const primaryDrivers = buildPrimaryDriverFilter(id, label);
            if (primaryDrivers?.length) {
                filters.drivers = primaryDrivers;
                filters.containsDrivers = true;
            }
            break;
        }
        case GroupByOption.Outline: {
            const outlineSegmentFilterItems = buildOutlineFilterItems(id, label);
            if (outlineSegmentFilterItems?.length) {
                filters.outlineSegmentFilterItems = outlineSegmentFilterItems;
            }
            break;
        }
        case GroupByOption.PatientJourney: {
            const stage = resolveJourneyStage(label);
            if (stage !== undefined) {
                filters.patientJourneyStages = [stage];
            }
            break;
        }
        case GroupByOption.EddyEffect: {
            const eddyEffectStatus = resolveEddyEffect(label);
            if (eddyEffectStatus !== undefined) {
                filters.eddyEffectStatus = eddyEffectStatus;
            }
            break;
        }
        case GroupByOption.CallDirection: {
            const callDirection = resolveCallDirection(label);
            if (callDirection !== undefined) {
                filters.callDirections = [callDirection];
            }
            break;
        }
        case GroupByOption.ContactType:
            if (label) {
                filters.contactTypes = [
                    resolveContactType(label, applicationFilterValuesStore),
                ];
            }
            break;
        case GroupByOption.Classifiers: {
            const rbcFilterItems = buildClassifierFilter(id);
            if (rbcFilterItems?.length) {
                filters.rbcFilterItems = rbcFilterItems;
            }
            break;
        }
        case GroupByOption.ModelResults:
            if (groupByLlmClassifierId && label) {
                // ApplicationFilterValuesStore.llmClassifiers is lazy-loaded when the
                // Model Results filter section is opened, so it may be undefined here.
                // OrgOptionsStore.llmClassifiers is loaded eagerly for chart configuration,
                // so prefer it and fall back to ApplicationFilterValuesStore.
                const llmClassifier =
                    optionsStore?.llmClassifiers?.find(
                        (classifier) => classifier.id === groupByLlmClassifierId,
                    ) ??
                    applicationFilterValuesStore?.llmClassifiers?.find(
                        (classifier) => classifier.id === groupByLlmClassifierId,
                    );
                filters.modelResults = [
                    {
                        id: groupByLlmClassifierId,
                        name: llmClassifier?.name ?? "",
                        results: [label],
                    },
                ];
            }
            break;
        case GroupByOption.EndingSentiment: {
            const endingSentiment = resolveSentiment(label);
            if (endingSentiment !== undefined) {
                filters.endingSentiment = endingSentiment;
            }
            break;
        }
        case GroupByOption.StartingSentiment: {
            const beginningSentiment = resolveSentiment(label);
            if (beginningSentiment !== undefined) {
                filters.beginningSentiment = beginningSentiment;
            }
            break;
        }
        case GroupByOption.HipaaCompliance: {
            const hipaaCompliance = resolveHipaaCompliance(label);
            if (hipaaCompliance !== undefined) {
                filters.hipaaCompliance = hipaaCompliance;
            }
            break;
        }
        case GroupByOption.SafetyEvent: {
            const safetyEvent = resolveSafetyEvent(label);
            if (safetyEvent) {
                filters.safetyEvent = safetyEvent;
            }
            break;
        }
        case GroupByOption.QuestionResult: {
            // The selection's identifier is the answer option's tag id, which is exactly what the
            // Question Result filter selects on, so the drilldown reproduces the bar as a filter
            // rather than approximating it from the label. The filter's shape needs both the module
            // and the question, and `normalizeGroupByConfiguration` rejects a persisted grouping
            // missing either, so this guard should never fire.
            if (!groupByLicensedModuleId || !groupByQuestionId || !id) break;

            filters.questionResults = [
                {
                    licensedModuleId: groupByLicensedModuleId,
                    questionId: groupByQuestionId,
                    tagIds: [id],
                },
            ];
            break;
        }
        case GroupByOption.ExtendedMetadata: {
            if (!groupByExtendedMetadataKey || !label) break;
            // Mirror how the filter UI writes groupable extended metadata: both the
            // grouped include-mode entry and a flat string representation, so backends
            // and UIs that read either shape stay in sync.
            filters.extendedMetadataGrouped = {
                ...(filters.extendedMetadataGrouped ?? {}),
                [groupByExtendedMetadataKey]: {
                    contains: true,
                    values: [label],
                },
            };
            filters.extendedMetadata = {
                ...(filters.extendedMetadata ?? {}),
                [groupByExtendedMetadataKey]: label,
            };
            break;
        }
        default:
            break;
    }
};

export const clearGroupingPayloadFromSignalsRequest = (
    state: SignalsRequest<Record<string, unknown>>,
) => {
    delete state.groupByOption;
    delete state.groupByIds;
    delete state.groupByLlmClassifierId;
    delete state.groupByExtendedMetadataKey;
};

export const normalizeSelectionForDrilldownStrategy = ({
    source,
    selection,
}: Pick<GroupingContext, "source" | "selection">) =>
    getStrategySelection(source, selection);

export const applyGroupingSelectionToFilters = ({
    source,
    selection,
    filters,
    applicationFilterValuesStore,
    optionsStore,
}: GroupingContext & {
    filters: ApplicationFilters;
}) => {
    applyGroupingSelectionToFiltersInternal({
        source,
        selection,
        filters,
        applicationFilterValuesStore,
        optionsStore,
    });
};

export const applyGroupingSelectionToRequestState = ({
    source,
    selection,
    state,
    applicationFilterValuesStore,
    optionsStore,
}: GroupingContext & {
    state: SignalsRequest<Record<string, unknown>>;
}) => {
    const grouping = getActiveGrouping(source);
    if (!grouping) {
        return;
    }

    applyGroupingSelectionToFiltersInternal({
        source,
        selection,
        filters: state as ApplicationFilters,
        applicationFilterValuesStore,
        optionsStore,
    });

    clearGroupingPayloadFromSignalsRequest(state);
};
